<template>
  <v-container>
    <loader :isLoading="isLoading"></loader>
    <v-form ref="form" @submit.prevent="onSave">
      <v-card elevation="2">
        <v-card-text>
          <widget-component v-for="(widgetMetadata, index) in formData.widgets"
            :key="index"
            :widgetMetadata="widgetMetadata"
          >
          </widget-component>
          <!-- start of protocol number -->
          <v-card class="mx-auto">
            <v-card-title>
              {{ protocolNumberTableMetadata.title }}
              <v-spacer></v-spacer>
              <v-btn
                color="primary"
                dark
                text
                class="mb-2"
                @click="onClickShowProtocolNumberSelection()"
              >
                <v-icon>mdi-plus</v-icon>
                {{ protocolNumberTableMetadata.addButtonName }}
              </v-btn>
            </v-card-title>
            <v-card-text>
              <ul style="list-style-type: none; margin: 0px; padding: 0px;">
                <draggable
                  handle=".handle"
                  :list="protocolNumberTableMetadata.items"
                >
                  <li
                    v-for="(item, index) in protocolNumberTableMetadata.items"
                    :key="`pn${index}`"
                  >
                    <v-btn text class="handle"><v-icon>mdi-menu</v-icon></v-btn>
                    <span class="text">{{ item.protocolNumber }}</span>
                    <v-btn text class="close">
                      <v-icon
                        class="mr-2"
                        color="primary"
                        @click="onClickDelete(item)"
                      >
                        mdi-delete
                      </v-icon>
                    </v-btn>
                    <v-btn
                      text
                      class="close"
                      @click="onClickShowAnswerForm(item)"
                    >
                      <v-icon
                        class="mr-2"
                        color="pink"
                      >
                        mdi-pencil
                      </v-icon>
                    </v-btn>
                  </li>
                </draggable>
              </ul>
            </v-card-text>
          </v-card>
          <!-- end of protocol number -->
          <v-btn
            style="margin-top: 20px;"
            color="primary"
            type="submit"
          >
            Save
          </v-btn>
        </v-card-text>
      </v-card>
    </v-form>
    <v-dialog
      v-model="answerFormMetaData.isOpen"
      fullscreen
      hide-overlay
      persistent
      transition="dialog-bottom-transition">
      <v-card>
        <v-toolbar
          dark
          color="primary"
        >
          <v-btn
            icon
            dark
            @click="closeAnswerForm()"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>
            <small>{{ caseNumber?.caseNumber }}</small>
            <v-btn icon dark>
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
            {{ answerFormMetaData.title }}
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-toolbar-items>
            <v-btn
              dark
              text
              v-if="!answerFormMetaData.isReadOnly"
              @click="onClickSaveToolBarForm()"
            >
              <!-- <v-icon>mdi-content-save</v-icon> -->
              save
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>

        <v-alert
          dense
          border="left"
          type="warning"
          v-if="answerFormMetaData.isReadOnly"
        >
          You only have <strong>READONLY</strong> access
        </v-alert>

        <!-- Form Display -->
        <v-stepper v-model="answerFormMetaData.e6" vertical>
          <div v-for="(form, index) in answerFormMetaData.forms" :key="index">
            <v-stepper-step :complete="answerFormMetaData.e6 > index+1" :step="index+1" editable>
              {{ form.name }}
            </v-stepper-step>
            <v-stepper-content :step="index+1">
              <v-card>
                <v-card-text>
                  <v-form :ref="`form${index}`">
                    <form-display
                      v-if="form.name !== 'Initial'"
                      :questionList="answerFormMetaData.questionRawList"
                      :isReadOnly="answerFormMetaData.isReadOnly"
                      :formMetadata="form"
                      :ref="`formDisplay${index}`"
                    >
                    </form-display>
                  </v-form>
                </v-card-text>
              </v-card>
            </v-stepper-content>
          </div>
        </v-stepper>
      </v-card>
    </v-dialog>

    <v-navigation-drawer
      v-model="isSelectingProtocolNumbers"
      fixed
      right
      temporary
      width="650px"
    >
      <v-list-item>
        <h3>{{ navDrawerSelectionMetadata.protocolNumber.title }}</h3>
      </v-list-item>

      <v-divider></v-divider>

      <v-col cols="12">
        <v-card>
          <v-card-title>
            {{ navDrawerSelectionMetadata.protocolNumber.tableTitle }}
            <v-spacer></v-spacer>
            <v-text-field
              v-model="navDrawerSelectionMetadata.protocolNumber.search"
              append-icon="mdi-magnify"
              label="Search"
              single-line
              hide-details
            ></v-text-field>
          </v-card-title>
          <v-data-table
            v-model="navDrawerSelectionMetadata.protocolNumber.selected"
            :headers="navDrawerSelectionMetadata.protocolNumber.headers"
            :items="navDrawerSelectionMetadata.protocolNumber.items"
            :items-per-page="5"
            :search="navDrawerSelectionMetadata.protocolNumber.search"
            item-key="_id"
            show-select
            class="elevation-1"
          >
            <template v-slot:[`item.actions`]="{ item }">
              <v-icon
                class="mr-2"
                @click="onClickViewFormPreview(item)"
                color="blue"
              >
                mdi-eye
              </v-icon>
            </template>
          </v-data-table>
        </v-card>
        <v-btn
          style="margin-top: 20px;"
          color="primary"
          @click="onClickAddSelectedProtocolNumberItems"
        >
          Add
        </v-btn>
      </v-col>
    </v-navigation-drawer>
  </v-container>
</template>

<!-- eslint-disable no-underscore-dangle -->
<script>
import draggable from 'vuedraggable';
import Loader from '../../../components/utilities/Loader.vue';
import WidgetComponent from '../../../components/ui/WidgetComponent.vue';

import FormDisplay from '../form/display/FormDisplay.vue';

import FormService from '../../../services/medical_registry/form.service';
import services from '../../../services';

const { medicalRegistry, controlPanelRegistry, authService } = services;
const {
  protocolNumberService,
  formService,
  questionService,
  specimenSourceService,
} = medicalRegistry;

const {
  specialRoleService,
} = controlPanelRegistry;

export default {
  components: {
    Loader,
    draggable,
    FormDisplay,
    WidgetComponent,
  },
  props: {
    isLoading: Boolean,
    onSubmit: Function,
    caseNumber: Object,
  },
  data() {
    return {
      isSelectingProtocolNumbers: false,
      formDialog: false,
      specimenSource: [],
      specimenSourceDefault: {
        // default specimen source value 'Human'
        _id: '651b63d7831587428f2d5cf1',
      },
      answerFormMetaData: {
        isOpen: false,
        isReadOnly: false,
        title: '',
        e6: 1,
        questionRawList: [],
        forms: [{ name: 'Initial' }],
      },
      formData: {
        model: this.caseNumber,
        widgets: [
          {
            type: 'text',
            name: 'caseNumber',
            label: 'Case Number',
            placeholder: 'Enter case number (e.g., Patient ID, Species ID, or Environmental ID)',
            value: '',
            counter: 120,
            maxLength: 120,
            rules: [
              (v) => !!v || 'Case Number is required',
              (v) => (v && v.length <= 120) || 'Case Number must be less than or equal to 100 characters',
            ],
          },
          {
            type: 'select',
            name: 'specimenSource',
            label: 'Specimen Source',
            placeholder: 'Select Specimen Source',
            value: '',
            items: [],
            counter: 120,
            maxLength: 120,
            rules: [
              (v) => !!v || 'Specimen Source is required',
            ],
          },
        ],
      },
      protocolNumberTableMetadata: {
        search: '',
        title: 'Project Code',
        addButtonName: 'Add Project Code',
        headers: [
          { text: 'Protocol Number', value: 'protocolNumber' },
          { text: 'ID', value: '_id' },
          { text: 'Actions', value: 'actions', sortable: false },
        ],
        items: [],
      },
      navDrawerSelectionMetadata: {
        protocolNumber: {
          search: '',
          title: 'Select Project Code to use',
          tableTitle: 'Project Codes',
          headers: [
            { text: 'Project Codes', value: 'protocolNumber' },
            { text: 'ID', value: '_id' },
            { text: 'Actions', value: 'actions', sortable: false },
          ],
          items: [],
          selected: [],
        },
      },
    };
  },
  async created() {
    const protocolNumbers = await protocolNumberService.getList();
    this.specimenSource = await specimenSourceService.getList();
    this.navDrawerSelectionMetadata.protocolNumber.items = protocolNumbers;

    const questions = await questionService.getList();
    this.answerFormMetaData.questionRawList = questions;
    const specimenSource = this.formData.widgets.find((f) => f.name === 'specimenSource');
    specimenSource.items = this.specimenSourceToChoices();

    if (this.caseNumber) {
      this.formData.widgets.forEach((w) => {
        const widget = w;
        if (widget.name === specimenSource.name && !this.formData.model[widget.name]) {
          // default Human if no specimen source found
          widget.value = this.specimenSourceDefault._id;
        } else {
          widget.value = this.formData.model[widget.name];
        }
      });
      const user = authService.getSignedInUser();
      const selectedProtocolNumber = this.caseNumber.protocolNumbers
        .map((protocol) => ({
          ...protocolNumbers.find((_protocol) => protocol.id === _protocol._id),
          response: protocol,
        }));
      const isValidRole = await specialRoleService.validateRole(user);
      const filterPertocolPermission = selectedProtocolNumber.filter(
        (pn) => pn?.researchers && (
          pn.researchers.some((rs) => rs === user.id)
          || pn.principalInvestigator === user.id
          || pn._metadata.created.user._id === user.id
          || isValidRole
        ),
      );
      // The bug is removed, these comments will remain for future reference:
      //  - bug when saving the encoded info while using researcher or principal investigator
      //  - the data (project code) of saved encoded info removes from the case
      this.protocolNumberTableMetadata.items = filterPertocolPermission;
    } else {
      // default specimen source value 'Human'
      specimenSource.value = this.specimenSourceDefault._id;
    }
  },
  watch: {
    // eslint-disable-next-line object-shorthand
    'answerFormMetaData.isOpen'(value) {
      // console.log(value);
      if (value) {
        setTimeout(() => {
          Array.from(document.getElementsByClassName('v-dialog--active')).forEach((item) => {
            item.setAttribute('tabindex', '0');
          });
        }, 2500);
      }
    },
  },
  methods: {
    // stepper save button has its own save function
    onClickSaveToolBarForm() {
      // // temporary disable validation when saving as requested by Team
      // const formsCount = [...Array(this.answerFormMetaData.forms.length).keys()];
      // const invalidIndex = formsCount.findIndex(
      //    (index) => !this.$refs[`form${index}`][0].validate()
      // );

      // if (invalidIndex === -1) {
      //   this.saveAnswerForm();
      // }

      this.saveAnswerForm();
    },
    saveAnswerForm() { // big save button on click
      const data = this.getProtocolAnswer();
      this.answerFormMetaData.e6 = 1;
      this.answerFormMetaData.isOpen = false;

      const protocolNumberResponse = {
        id: this.answerFormMetaData.id,
        forms: data,
      };

      const medicalForm = this.protocolNumberTableMetadata
        .items
        .find((form) => form._id === protocolNumberResponse.id);
      medicalForm.response = protocolNumberResponse;
    },
    getFormDisplay(index) {
      return this.$refs[`formDisplay${index}`][0];
    },
    getProtocolAnswer() {
      return this.answerFormMetaData.forms
        .map((_form, index) => this.getFormDisplay(index).getData());
    },
    isLast(size, index) {
      return size === (index + 1);
    },
    async onClickShowAnswerForm(item) {
      this.answerFormMetaData.isOpen = true;
      this.answerFormMetaData.answerFormMetaData = false;
      this.answerFormMetaData.id = item._id;
      this.answerFormMetaData.title = item.protocolNumber;
      const getQuery = item.medicalForms
        .map((formId) => formService.getOne({ id: formId }));
      const forms = await Promise.all(getQuery);

      const user = authService.getSignedInUser();
      let isPrincipalInvestigator = false;
      let isResearchers = false;
      // Verify user inclusion as researcher or principal investigator in project code.
      isPrincipalInvestigator = item.principalInvestigator.includes(user.id);
      isResearchers = item.researchers.some((re) => re === user.id);
      const isValidRole = await specialRoleService.validateRole(user);
      // Enable readonly if neither the two is true
      this.answerFormMetaData.isReadOnly = !isPrincipalInvestigator
        && !isResearchers
        && !isValidRole;

      // answers are flattened away from section
      // inflating section by the flattened answers
      const setQuestionValue = (questions, code, value) => {
        questions.forEach((question) => {
          if (question.display === 'section') {
            const section = question;
            setQuestionValue(section.questions, code, value);
          } else if (question.code === code) {
            question.value = value;
          }
        });
      };

      // add value field in question
      this.answerFormMetaData.forms = forms.map((form, index) => {
        const previousAnswers = item.response?.forms?.find((res) => res.id === form._id);
        Object.keys(previousAnswers?.answer || {}).forEach((code) => {
          setQuestionValue(form.questions, code, previousAnswers.answer[code]);
        });
        return {
          ...form,
          button: {
            label: !this.isLast(forms.length, index) ? 'next' : 'save',
            onClick: () => {
              // // temporary disable validation as requested by team
              // const vueForm = this.$refs[`form${index}`][0];
              // if (vueForm.validate()) {
              //   if (!this.isLast(forms.length, index)) {
              //     this.answerFormMetaData.e6 += 1;
              //   } else {
              //     this.saveAnswerForm();
              //   }
              // }
              if (!this.isLast(forms.length, index)) {
                this.answerFormMetaData.e6 += 1;
              } else {
                this.saveAnswerForm();
              }
            },
          },
        };
      });
    },
    closeAnswerForm() {
      const closeAnswerFormDialog = () => {
        this.answerFormMetaData.e6 = 1;
        this.answerFormMetaData.isOpen = false;
      };
      if (!this.answerFormMetaData.isReadOnly) {
        this.$confirmDialog.show({
          text: 'Are you sure you want to close without saving?',
          title: 'Confirm Close',
        }, () => {
          closeAnswerFormDialog();
        });
      } else {
        closeAnswerFormDialog();
      }
    },
    onClickDelete(item) {
      this.$confirmDialog.show({
        text: `<p>Are you sure you want to delete: <b>${item.protocolNumber}</b></p>`,
        title: 'Confirm Delete',
      }, () => {
        const tableMetaData = this.protocolNumberTableMetadata;

        const filtered = tableMetaData.items
          .filter((data) => data._id !== item._id);
        tableMetaData.items = filtered;

        this.$snackbar.show({ color: 'green', text: 'Deleted successfully', closeColor: 'white' });
      });
    },
    // onClickDeleteProtocolNumber(item) {
    //   this.$confirmDialog.show({
    //     text: `<p>Are you sure you want to delete: <b>${item.protocolNumber}</b></p>`,
    //     title: 'Confirm Delete',
    //   }, () => {
    //     const filtered = this.protocolNumberTableMetadata.items
    //       .filter((protocol) => protocol._id !== item._id);
    //     this.protocolNumberTableMetadata.items = filtered;
    //   });
    //   this.$snackbar.show();
    // },
    onClickAddSelectedProtocolNumberItems() {
      // console.log(this.navDrawerSelectionMetadata.protocolNumber.selected, 'selected');
      const selected = [...this.navDrawerSelectionMetadata.protocolNumber.selected];
      this.navDrawerSelectionMetadata.protocolNumber.selected = [];
      selected.forEach((form) => {
        const isPresent = this.protocolNumberTableMetadata.items
          .find((f) => f._id === form._id);
        if (!isPresent) {
          this.protocolNumberTableMetadata.items.push(form);
        }
      });
      this.isSelectingProtocolNumbers = false;
    },
    onClickShowProtocolNumberSelection() {
      this.isSelectingProtocolNumbers = true;
    },
    onSave() { // save the data
      if (this.$refs.form.validate() && (this.protocolNumberTableMetadata.items.length > 0)) {
        const dataChanges = FormService.dataAsJSON(this.formData);
        dataChanges.protocolNumbers = this.protocolNumberTableMetadata
          .items
          .map((protocol) => ({
            id: protocol._id,
            forms: protocol.response?.forms,
          }));
        const data = Object.assign(this.caseNumber || {}, dataChanges);
        this.onSubmit(data);
      } if (this.protocolNumberTableMetadata.items.length === 0) {
        this.$confirmDialog.show({
          title: 'Save Error',
          text: `
            <ul>
              <li>Must have at least one Project Code selected</li>
            </ul>
          `,
          isMessage: true,
        });
      }
    },
    specimenSourceToChoices() {
      return this.specimenSource.map(
        (source) => ({
          code: source._id,
          label: source.name,
        }),
      );
    },
  },
};
</script>

<style scoped>
.close {
  float: right;
  padding-top: 8px;
  padding-bottom: 8px;
}
.text {
  font-weight: bold;
  margin: 20px;
}
</style>
